import React, { useState, useEffect, useContext, useCallback } from 'react';
import 'react-quill/dist/quill.snow.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { AppContext } from '../util/AppContext';
import { getConfigValue } from '../util/getConfigValue';
import AlertModal from '../components/AlertModal';
import { getAnnoucements, saveAnnoucement } from '../API/announcements';
import { v4 as uuid } from 'uuid';
import { useBreadcrumbs } from '../util/BreadcrumbContext';
import TextEditor from '../components/TextEditor';
import PageTitle from '../components/PageTitle';

export default function SystemAnnouncementManagementPage() {
	const [appState] = useContext(AppContext);
	const [annoucementId, setAnnoucementId] = useState(uuid());
	const [content, setContent] = useState();
	const [annoucementsEnabled, setAnnoucementsEnabled] = useState(false);
	const [previousState, setPreviousState] = useState({ id: null, enabled: false, content: null });
	const [userRole] = useState(appState.role);
	const { state } = useLocation();
	const [alert, setAlert] = useState(state?.alert || null);
	const navigate = useNavigate();
	const maxAnnouncementCharacterCount = getConfigValue(appState?.config?.SystemAnnouncementManagement?.MaxCharacterCount, appState)?.Value || 1700;
	const [hasPendingChange, setHasPendingChange] = useState(false);
	const announcmentPartition = 'System';
	const { setBreadcrumbs } = useBreadcrumbs();
	const [rawCharacterCount, setRawCharacterCount] = useState(0);
	const adminSystemAnnouncementManagementEnabled = getConfigValue(appState?.config?.Admin?.SystemAnnouncementManagement?.Enabled, appState)?.Value === 'true';

	useEffect(() => {
		setBreadcrumbs([
			{ label: getConfigValue(appState?.config?.SystemAnnouncementManagement?.Title, appState)?.Value || 'System Announcement Management', path: '' },
		]);
	}, [setBreadcrumbs]);

	const userNavigation = useCallback(() => {
		if (adminSystemAnnouncementManagementEnabled === false || (userRole !== 'Admin' && userRole !== 'Precision')) {
			navigate('/');
		}
	}, [adminSystemAnnouncementManagementEnabled, userRole, navigate]);

	useEffect(() => {
		userNavigation();
		const getAnnouncementData = async () => {
			await getAnnoucements(announcmentPartition)
				.then((responseJson) => {
					if(!responseJson?.length) {
						return;
					}
					setContent(responseJson[0].content);
					setAnnoucementsEnabled(responseJson[0].isActive);
					setAnnoucementId(responseJson[0].id);
					setPreviousState({ id: responseJson[0].id, enabled: responseJson[0].isActive, content: null });
				})
				.catch((err) => {});
		};
		getAnnouncementData();
	}, [adminSystemAnnouncementManagementEnabled, userRole, userNavigation]);

	useEffect(() => {
		if (alert !== null) {
			setTimeout(() => {
				setAlert(null);
			}, 10000);
		}
	}, [alert]);

	function handleAnnoucementsEnableChecked(event) {
		setHasPendingChange(previousState.enabled !== event.target.checked);
		setAnnoucementsEnabled(event.target.checked);
	}

	const handleSaveAnnoucementClick = async (event) => {
		const formatedContent = formatContent(content);

		if (rawCharacterCount === 0) {
			setAlert({
				type: 'danger',
				heading: getConfigValue(appState?.config?.AnnouncementManagement?.Failure?.Title, appState)?.Value || null,
				message: getConfigValue(appState?.config?.AnnouncementManagement?.Messages?.Save?.Error, appState)?.Value || null,
			});
			return;
		}

		await saveAnnoucement(announcmentPartition, annoucementId, annoucementsEnabled, formatedContent)
			.then((responseJson) => {
				setAnnoucementId(responseJson.id);
				setPreviousState({ id: responseJson.id, enabled: annoucementsEnabled, content: formatedContent });
				setAlert({
					type: 'success',
					heading: getConfigValue(appState?.config?.AnnouncementManagement?.Success?.Title, appState)?.Value || null,
					message: getConfigValue(appState?.config?.AnnouncementManagement?.Messages?.Save?.Success, appState)?.Value || null,
				});
			})
			.catch((err) => {
				setAlert({
					type: 'danger',
					heading: getConfigValue(appState?.config?.AnnouncementManagement?.Failure?.Title, appState)?.Value || null,
					message: getConfigValue(appState?.config?.AnnouncementManagement?.Messages?.Save?.Error, appState)?.Value || null,
				});
			});
	};

	function formatContent(content) {
		content = content.replace(/rgb\((\d+),\s*(\d+),\s*(\d+)\)(?!\s*;)/g, 'rgba($1, $2, $3, 1)');

		return content;
	}

	function handleCancelClick(event) {
		setHasPendingChange(false);
		setAnnoucementsEnabled(previousState.enabled);
		setContent(previousState.content);
		setAnnoucementId(previousState.id);
	}

	return (
		<>
			{alert && (
				<div className='container-xxl mt-4'>
					<div className='row justify-content-center'>
						<div className='col-12 col-md-10 col-lg-8'>
							<AlertModal alert={alert} />
						</div>
					</div>
				</div>
			)}
			<div className='container-xxl mt-4'>
				<div className='row justify-content-center'>
					<div className='col-12 col-md-10 col-lg-8'>
						<div className='my-0'>
							<PageTitle
								title={getConfigValue(appState?.config?.SystemAnnouncementManagement?.Title, appState)?.Value || 'Announcement Management'}
							/>
						</div>
					</div>
				</div>
			</div>
			<div className='container-xxl mt-4'>
				<div className='row justify-content-center'>
					<div className='col-12 col-md-10 col-lg-8'>
						<div className='card pd-card py-3 mb-5'>
							<div className='container'>
								<div className='d-flex justify-content-between align-items-center mb-3'>
									<h2 className='type-20 type-bold mb-0'>
										{getConfigValue(appState?.config?.SystemAnnouncementManagement?.Form?.Title, appState)?.Value || 'Announcement Banner'}
									</h2>
								</div>
								<div className='form-check mb-3 mb-md-4'>
									<input
										className='form-check-input'
										type='checkbox'
										checked={annoucementsEnabled}
										onChange={handleAnnoucementsEnableChecked}
										id={`annoucements-enabled-checkbox`}
									></input>
									<label className='form-check-label type-14' htmlFor={`annoucements-enabled-checkbox`}>
										{getConfigValue(appState?.config?.SystemAnnouncementManagement?.Form?.IsActive?.Label, appState)?.Value || 'Active'}
									</label>
								</div>
								<div className='mb-2'>
									<label className='form-check-label type-14' htmlFor={`toolbar`}>
										{getConfigValue(appState?.config?.SystemAnnouncementManagement?.Form?.Content?.Label, appState)?.Value || 'Message'}
									</label>
								</div>
								<TextEditor
									content={content}
									setContent={setContent}
									maxAnnouncementCharacterCount={maxAnnouncementCharacterCount}
									rawCharacterCount={rawCharacterCount}
									setRawCharacterCount={setRawCharacterCount}
									setHasPendingChange={setHasPendingChange}
								/>
							</div>
						</div>
						<div className='row justify-content-center justify-content-md-end'>
							<div className='col-12 col-sm-8 col-md-8 col-lg-7 col-xl-6'>
								<div className='d-flex flex-column flex-md-row-reverse'>
									<button
										type='submit'
										onClick={handleSaveAnnoucementClick}
										className='btn btn-success btn-lg mb-3 mb-md-0 ms-md-3 flex-fill w-100'
									>
										{getConfigValue(appState?.config?.AnnouncementManagement?.Form?.Button?.Save?.Label, appState)?.Value || 'Save'}
									</button>
									<button
										type='submit'
										disabled={!hasPendingChange}
										onClick={handleCancelClick}
										className='btn btn-secondary btn-lg flex-fill w-100'
									>
										{getConfigValue(appState?.config?.Common?.Cancel, appState)?.Value || 'Cancel'}
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}
